import { getCurrentUser, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import axios from 'axios';

import awsconfig from '../config/aws';
import { clearTrackId, trackId } from '../utils/tracker';
import { getUserEmail } from '../utils/user';

export const checkUser = async () => {
  const {
    oauth: { domain, scope, responseType, auth0_identity_provider, redirectSignIn },
    aws_user_pools_web_client_id,
  } = awsconfig;

  try {
    // First check if user exists
    const user = await getCurrentUser();
    // Then get the session
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken;

    if (!accessToken) {
      throw new Error('No access token');
    }

    const tokenExpiry = accessToken.payload.exp as number;
    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = tokenExpiry <= currentTime;

    if (isExpired) {
      // Try to refresh the token
      const refreshedSession = await fetchAuthSession({ forceRefresh: true });
      const refreshedToken = refreshedSession.tokens?.accessToken;

      if (!refreshedToken) {
        throw new Error('Token refresh failed');
      }

      axios.defaults.headers.common['Authorization'] = 'Bearer ' + refreshedToken.toString();
    } else {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken.toString();
    }

    if (user) {
      trackId(getUserEmail(user.username));
    }

    return {
      username: user.username,
      userId: user.userId,
    };
  } catch (error) {
    console.error('checkUser: Authentication failed:', error);

    // Save current path for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    const shouldSaveRedirect =
      currentPath !== '/' && !currentPath.includes('code=') && !currentPath.includes('error=');

    if (shouldSaveRedirect) {
      sessionStorage.setItem('redirectAfterLogin', currentPath);
    }

    try {
      // Use Amplify's signInWithRedirect for proper OAuth handling
      await signInWithRedirect({
        provider: {
          custom: auth0_identity_provider,
        },
      });
    } catch (redirectError) {
      console.error('checkUser: signInWithRedirect failed, using manual redirect:', redirectError);

      // Fallback to manual redirect
      const url = new URL(
        `https://${domain}/oauth2/authorize?identity_provider=${auth0_identity_provider}&redirect_uri=${redirectSignIn}&response_type=${responseType.toUpperCase()}&client_id=${aws_user_pools_web_client_id}&scope=${scope.join(' ')}`
      );

      window.location.assign(url.toString());
    }

    return;
  }
};

export const logout = async () => {
  const {
    oauth: { domain, scope, responseType, redirectSignOut },
    aws_user_pools_web_client_id,
  } = awsconfig;

  clearTrackId();
  // Clear any old cognito session if it is not cleared by Auth.SignOut
  const items = { ...localStorage };
  for (const a in items) {
    if (a.startsWith('CognitoIdentityServiceProvider') || a.includes('amplify')) {
      localStorage.removeItem(a);
      localStorage.removeItem('userLoggedForFirstTime');
    }
  }
  const url = new URL(
    `https://${domain}/logout?response_type=${responseType}&client_id=${aws_user_pools_web_client_id}&redirect_uri=${redirectSignOut}&state=STATE&scope=${scope.join(
      ' '
    )}`
  );
  window.location.assign(url.toString());
};
